import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON } from "build-ts/common";
import { AppIcons } from "constants/icons";
import { ArtifactOpenOpts, SelectedArtifactItem } from "modules/interfaces";
import { IconType } from "react-icons/lib/iconBase";
import { TAB_ID } from "../dataframe/utils/dataFrameCodeUtils";
import { ARTIFACT_TAG_TYPE, buildWorkflowTag } from "modules/chat/utils/chatUtils";

/**
 * Discriminant type for the nested artifact registry's outer key.
 * Derived from ARTIFACT_TAG_TYPE so it stays in sync automatically when new
 * tag types are added — no manual update needed here.
 *
 *   "code"         — DATAFRAME_ANALYSIS top-level (the analysis itself)
 *   "dataframe"    — pandas_df sub-resources + KG_DATAFRAME
 *   "chart"        — chart sub-resources
 *   "report"       — report sub-resources
 *   "workflow"     — SCENARIO_JOB
 *   "workflow-run" — ANALYSIS_OUTPUT
 */
export type ArtifactKind = (typeof ARTIFACT_TAG_TYPE)[keyof typeof ARTIFACT_TAG_TYPE];

/**
 * Nested artifact registry — mirrors the backend's map<artifact_type, map<artifact_name, Artifact>>.
 * Using a nested map ensures that artifacts of different types can share the same name
 * without one overwriting the other (e.g. a "code" artifact and a "dataframe" artifact both
 * named "results" are stored and listed separately).
 */
export type ArtifactMap = Map<ArtifactKind, Map<string, ArtifactProto>>;

/**
 * Returns a stable identity string for an artifact, derived from its header id.
 * Used for deduplication and caching across allArtifacts and selectedArtifacts.
 */
export function getArtifactHeaderId(artifact: ArtifactProto): string {
  if (!artifact) return "";
  return (
    artifact.dataframe_analysis?.header?.id ||
    artifact.kg_dataframe?.header?.id ||
    artifact.scenario_job?.header?.id ||
    artifact.analysis_output?.header?.id ||
    ""
  );
}

/**
 * Returns the artifact's header name.
 * Used as the key for the name-based artifact map and name index.
 */
export function getArtifactHeaderName(artifact: ArtifactProto): string {
  if (!artifact) return "";
  return (
    artifact.dataframe_analysis?.header?.name ||
    artifact.kg_dataframe?.header?.name ||
    artifact.scenario_job?.scheduled_analysis?.darwin_name ||
    artifact.analysis_output?.header?.name ||
    ""
  );
}

/**
 * Safe wrapper around artifactTypeFromJSON that handles undefined artifacts.
 * Replaces the `artifactTypeFromJSON(a?.artifact_type ?? "0")` pattern throughout the codebase.
 */
export function getArtifactType(artifact: ArtifactProto): ArtifactType {
  return artifactTypeFromJSON(artifact?.artifact_type);
}

/**
 * Maps an artifact to its ArtifactKind bucket key — the same key used in ArtifactMap.
 * Use this when you already hold the artifact and need to call lookupByName with the
 * correct kind for a precise (type, name) lookup.
 */
export function getArtifactKind(artifact: ArtifactProto): ArtifactKind {
  const type = getArtifactType(artifact);
  if (type === ArtifactType.KG_DATAFRAME) return ARTIFACT_TAG_TYPE.dataframe;
  if (type === ArtifactType.DATAFRAME_ANALYSIS) return ARTIFACT_TAG_TYPE.code;
  if (type === ArtifactType.SCENARIO_JOB) return ARTIFACT_TAG_TYPE.workflow;
  return ARTIFACT_TAG_TYPE.workflowRun;
}

/**
 * Returns the artifact's tab key, used to identify the artifact tab.
 */
export function getArtifactTabKey(item: SelectedArtifactItem): string {
  const type = item.artifact ? artifactTypeFromJSON(item.artifact.artifact_type) : null;
  if (type === ArtifactType.ANALYSIS_OUTPUT && item.analysisGroupId) {
    return `group-${item.analysisGroupId}`;
  }
  return `item-${item.orderIndex}`;
}

/**
 * Returns the artifact's label, normalized to lowercase+trimmed.
 * Used as the label for the artifact tab.
 */
export function getArtifactLabel(item: ArtifactProto): string {
  const type = artifactTypeFromJSON(item.artifact_type);
  if (type === ArtifactType.KG_DATAFRAME) return item.kg_dataframe?.header?.name ?? "DataFrame";
  if (type === ArtifactType.SCENARIO_JOB) return item.scenario_job?.header?.name ?? "Scenario";
  if (type === ArtifactType.KG_OBSERVATION) return "KG Observation";
  if (type === ArtifactType.DATAFRAME_ANALYSIS) return item.dataframe_analysis?.header?.name ?? "DF Analysis";
  if (type === ArtifactType.ANALYSIS_OUTPUT) return "Analysis";
  return "Artifact";
}

/** Minimal interface for a Vega View used by the export utilities. */
export interface VegaView {
  toImageURL: (format: string, scale?: number) => Promise<string>;
}

/**
 * Renders a Vega view to a data URL in the requested format.
 *
 * For JPEG, the PNG is first rendered at 2× scale and then composited over a
 * white canvas before encoding, because JPEG has no alpha channel and
 * transparent pixels would otherwise be encoded as black.
 */
export async function vegaViewToDataURL(view: VegaView, format: "png" | "jpeg"): Promise<string> {
  const pngUrl = await view.toImageURL("png", 2);

  if (format === "png") {
    return pngUrl;
  }

  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = pngUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  // JPEG is always lossy; quality 1.0 minimises compression artefacts.
  return canvas.toDataURL("image/jpeg", 1.0);
}

/**
 * Returns the artifact's icon, used to display the artifact tab.
 */
export function getArtifactIcon(artifact: ArtifactProto): IconType {
  const type = artifactTypeFromJSON(artifact.artifact_type);
  if (type === ArtifactType.KG_DATAFRAME) return AppIcons.database;
  if (type === ArtifactType.DATAFRAME_ANALYSIS) return AppIcons.mathFunction;
  if (type === ArtifactType.ANALYSIS_OUTPUT) return AppIcons.search;
  return AppIcons.workflow;
}

/**
 * A single entry that represents an artifact (or sub-resource) in a browsable
 * or mention-able list.
 *
 * - `tag`       – the [[type: name]] string inserted into the chat input.
 * - `artifacts` – the actual ArtifactProto(s) to open; usually 1, except for
 *                 ANALYSIS_OUTPUT groups where multiple share a job_id.
 * - `opts`      – optional panel-open options (activeTab, subResourceName).
 */
export type ArtifactEntry = {
  key: string;
  label: string;
  icon: IconType;
  tag: string;
  artifacts: ArtifactProto[];
  opts?: ArtifactOpenOpts;
};

/** A labelled group of ArtifactEntry items (Tables, Charts, Code, Workflows …). */
export type ArtifactSection = {
  id: string;
  label: string;
  items: ArtifactEntry[];
};

/**
 * Derive a flat list of browse/mention sections from the current session's
 * artifact map. Each bucket of the nested ArtifactMap maps directly to one
 * section, eliminating the need to distinguish sub-resource entries by name.
 *
 * Sections are returned in display order: Raw data → Tables → Charts → Code → Documents → Workflows → Workflow Runs.
 * Empty sections are omitted.
 *
 * Workflows are deduplicated by header id; workflow-run artifacts are grouped
 * by job_id so all runs for the same job appear under one entry.
 *
 * Used by both the ArtifactPanel browse menu and the @ mention menu in chat.
 */
export function buildArtifactSections(artifactMap: ArtifactMap): ArtifactSection[] {
  const rawData: ArtifactEntry[] = [];
  const tables: ArtifactEntry[] = [];
  const charts: ArtifactEntry[] = [];
  const code: ArtifactEntry[] = [];
  const documents: ArtifactEntry[] = [];
  // Keyed by job header id — deduplicates if the same SCENARIO_JOB appears under multiple names.
  const workflowsMap = new Map<string, ArtifactEntry>();
  // Keyed by job_id — each key represents one job's complete set of run artifacts.
  const runsMap = new Map<string, { artifacts: ArtifactProto[]; label: string }>();

  // "dataframe" bucket holds both KG_DATAFRAME (raw data) and pandas_df sub-resources (tables).
  for (const [name, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.dataframe)?.entries() ?? [])) {
    if (getArtifactType(artifact) === ArtifactType.KG_DATAFRAME) {
      rawData.push({
        key: artifact.kg_dataframe?.header?.id ?? name,
        label: getArtifactLabel(artifact),
        icon: AppIcons.database,
        tag: `[[dataframe: ${name}]]`,
        artifacts: [artifact],
      });
    } else {
      tables.push({
        key: name,
        label: name,
        icon: AppIcons.dataframe,
        tag: `[[dataframe: ${name}]]`,
        artifacts: [artifact],
        opts: { activeTab: TAB_ID.results, subResourceName: name },
      });
    }
  }

  for (const [name, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.chart)?.entries() ?? [])) {
    charts.push({
      key: name,
      label: name,
      icon: AppIcons.chart,
      tag: `[[chart: ${name}]]`,
      artifacts: [artifact],
      opts: { activeTab: TAB_ID.results, subResourceName: name },
    });
  }

  for (const [name, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.code)?.entries() ?? [])) {
    code.push({
      key: artifact.dataframe_analysis?.header?.id ?? name,
      label: name || "Analysis",
      icon: AppIcons.codeAlt,
      tag: `[[code: ${name}]]`,
      artifacts: [artifact],
      opts: { activeTab: TAB_ID.code },
    });
  }

  for (const [name, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.report)?.entries() ?? [])) {
    documents.push({
      key: name,
      label: name,
      icon: AppIcons.document,
      tag: `[[report: ${name}]]`,
      artifacts: [artifact],
      opts: { activeTab: TAB_ID.results, subResourceName: name },
    });
  }

  for (const [, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.workflow)?.entries() ?? [])) {
    const id = artifact.scenario_job?.header?.id!;
    const darwinName = artifact.scenario_job?.scheduled_analysis?.darwin_name;
    const headerName = getArtifactLabel(artifact);
    workflowsMap.set(id, {
      key: id,
      label: headerName,
      icon: AppIcons.workflow,
      tag: buildWorkflowTag(darwinName!, headerName),
      artifacts: [artifact],
    });
  }

  for (const [, artifact] of Array.from(artifactMap.get(ARTIFACT_TAG_TYPE.workflowRun)?.entries() ?? [])) {
    // Group all run artifacts for the same job under one entry, keyed by job_id.
    const id = `ao:${artifact.analysis_output?.job_id!}`;
    if (!runsMap.has(id)) {
      runsMap.set(id, {
        artifacts: [],
        label: `${workflowsMap.get(artifact.analysis_output?.job_id!)?.label} Runs`,
      });
    }
    runsMap.get(id)!.artifacts.push(artifact);
  }

  const runs: ArtifactEntry[] = Array.from(runsMap.entries()).map(([id, { artifacts, label }]) => ({
    key: id,
    label,
    icon: AppIcons.search as IconType,
    tag: `[[workflow-run: ${label}]]`,
    artifacts,
  }));

  const sections: ArtifactSection[] = [];
  if (rawData.length > 0) sections.push({ id: "raw-data", label: "Raw data", items: rawData });
  if (tables.length > 0) sections.push({ id: "tables", label: "Tables", items: tables });
  if (charts.length > 0) sections.push({ id: "charts", label: "Charts", items: charts });
  if (code.length > 0) sections.push({ id: "code", label: "Code", items: code });
  if (documents.length > 0) sections.push({ id: "documents", label: "Documents", items: documents });
  if (workflowsMap.size > 0)
    sections.push({ id: "workflows", label: "Workflows", items: Array.from(workflowsMap.values()) });
  if (runs.length > 0) sections.push({ id: "runs", label: "Workflow Runs", items: runs });
  return sections;
}

/**
 * Returns the ArtifactOpenOpts for a given tag/link type and name.
 * Used by both Message.tsx (user message tags) and MarkdownRenderer.tsx (AI message links)
 * to ensure consistent tab/pill navigation across all artifact link surfaces.
 */
export function getArtifactOpenOpts(tagType: string, name: string): ArtifactOpenOpts | undefined {
  switch (tagType) {
    case ARTIFACT_TAG_TYPE.code:
      return { activeTab: TAB_ID.code, kind: ARTIFACT_TAG_TYPE.code };
    case ARTIFACT_TAG_TYPE.dataframe:
      return { activeTab: TAB_ID.results, subResourceName: name, kind: ARTIFACT_TAG_TYPE.dataframe };
    case ARTIFACT_TAG_TYPE.chart:
      return { activeTab: TAB_ID.results, subResourceName: name, kind: ARTIFACT_TAG_TYPE.chart };
    case ARTIFACT_TAG_TYPE.report:
      return { activeTab: TAB_ID.results, subResourceName: name, kind: ARTIFACT_TAG_TYPE.report };
    default:
      return undefined;
  }
}

/** Icon and display prefix for each artifact tag type. */
export const ARTIFACT_TAG_META: Record<string, { icon: keyof typeof AppIcons; prefix: string }> = {
  [ARTIFACT_TAG_TYPE.workflow]: { icon: "workflow", prefix: "Workflow" },
  [ARTIFACT_TAG_TYPE.dataframe]: { icon: "dataframe", prefix: "Table" },
  [ARTIFACT_TAG_TYPE.chart]: { icon: "chart", prefix: "Chart" },
  [ARTIFACT_TAG_TYPE.code]: { icon: "codeAlt", prefix: "Code" },
  [ARTIFACT_TAG_TYPE.report]: { icon: "document", prefix: "Document" },
};
